/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useEffect, useRef, useState } from 'react';
import { Box } from 'native-base';
import {
    Stashboard,
    StashboardMedia,
    useAppSettings,
    useSendEvent,
    useSpringBigStore
} from '@springbig/shared';
import _ReactPlayer, { ReactPlayerProps } from 'react-player';
import Image from '~/shared/Image';
import { useAppNavigate } from '~/pages';
import VisibilitySensor from 'react-visibility-sensor';
import ClaimGiftCardModal from '~/shared/ClaimGiftCardModal';
import Pressable from '~/shared/Pressable';
import { Caption } from '~/shared/Typography';

const ReactPlayer = _ReactPlayer as unknown as React.FC<ReactPlayerProps>;

export const CarouselCard = ({
    item,
    index,
    stashboard,
    setImgHeight,
    currentIndex
}: {
    item: StashboardMedia,
    index: number,
    stashboard: Stashboard,
    setImgHeight: (height: number) => void,
    currentIndex: number
}) => {

    const { fontFamily } = useAppSettings();
    const [isVisible, setIsVisible] = useState(false);
    const { member } = useSpringBigStore();
    const { params, navigate } = useAppNavigate();
    const { mutate: sendEvent } = useSendEvent();

    if (stashboard?.full_image_content) {
        const imgRef = useRef(null);
        const [imgWidth, setImgWidth] = useState(0);

        useEffect(() => {

            const handleResize = () => {

                if (imgRef.current) {
                    setImgWidth(imgRef.current.clientWidth);
                    setImgHeight(imgRef.current.clientHeight);
                }
            };

            if (imgRef.current && imgRef.current.complete) {
                setImgWidth(imgRef.current.clientWidth);
                setImgHeight(imgRef.current.clientHeight);
            }

            window.addEventListener('resize', handleResize);
        }, [imgRef.current]);

        const handleImageLoad = () => {

            if (imgRef.current) {
                setImgWidth(imgRef.current.clientWidth);
                setImgHeight(imgRef.current.clientHeight);
            }
        };

        const [currentGiftCardRewardGrant, setCurrentGiftCardRewardGrant] = useState(null);

        return (
            <>
                <Box key={index} position={'relative'} w={'100%'}>

                    <img alt='' ref={imgRef} src={item.result_url} width={'100%'} onLoad={handleImageLoad} loading='lazy' />
                    {stashboard.full_image_content.map((full_image_content, index0) =>

                        full_image_content.buttons.map((button, index1) => {

                            const isGiftCard = button.ext_type === 'gift_card';
                            const rewardGrant = member?.reward_grants?.find((rg) => rg.reward.id === button.ext_reward_id);

                            const handlePress = () => {

                                if (member) {
                                    sendEvent({
                                        event: 'click',
                                        sbk: stashboard._key,
                                        event_owner_id: button.id,
                                        event_owner_type: 'StashboardImageButton',
                                        source: 'wallet',
                                    });
                                }

                                if (isGiftCard) {
                                    if (rewardGrant) {
                                        setCurrentGiftCardRewardGrant(rewardGrant.id);
                                    }
                                    else {
                                        navigate(`w/${params.wallet_id}/giftCard`);
                                    }
                                }
                                else {
                                    if (button.ext_type) {
                                        const target = button.target_url.split('/').splice(4);
                                        const page = target[1].split('?')[0];
                                        const param = target[1]?.match(/reward=(\d+)/);

                                        navigate(`w/${target[0]}/${page}/${param ? param[1] : ''}`);
                                    }
                                    else {
                                        window.open(button.target_url);
                                    }
                                }

                                if (merchant?.enhanced_reporting_enabled) {
                                    sendEvent({
                                        event: 'click',
                                        sbk: stashboard._key,
                                        event_owner_id: button.id,
                                        event_owner_type: 'StashboardImageButton',
                                        source: 'wallet',
                                        process_type: stashboard.owner_type,
                                        process_id: stashboard.owner_id,
                                    });
                                }
                            };

                            if (index0 !== index) {
                                return <Box key={`empty-${index0}, ${index1}`}></Box>;
                            }

                            return (
                                <Pressable
                                    key={`${index0}, ${index1}`}
                                    borderColor={button.border_color}
                                    borderWidth={1}
                                    position={'absolute'}
                                    w={`${button.width}%`}
                                    h={`${imgWidth * button.height / 100}px`}
                                    top={`${imgWidth * button.y / 100}px`}
                                    left={`${button.x}%`}
                                    backgroundColor={button.style === 'visible' ? button.background_color : 'transparent'}
                                    p={0}
                                    m={0}
                                    overflow={'hidden'}
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    borderRadius={button.border_radius || 0}
                                    onPress={handlePress}
                                >
                                    <Caption
                                        textAlign={'center'}
                                        fontSize={`${(button.font_size * imgWidth) / 100}px`}
                                        lineHeight={`${(button.font_size * imgWidth) / 100}px`}
                                        color={button.text_color}
                                        fontFamily={button.text_font ?? `${button.text_font}, ${fontFamily}`}
                                    >
                                        { button.text }
                                    </Caption>
                                </Pressable>
                            );
                        }
                        ))}
                </Box>
                {currentGiftCardRewardGrant &&
                    <ClaimGiftCardModal
                        rewardGrantId={currentGiftCardRewardGrant}
                        closeModal={() => setCurrentGiftCardRewardGrant(null)}
                    />
                }
            </>
        );
    }

    if (item?.media_type === 'video') {

        return (
            <VisibilitySensor onChange={(visibility) => visibility && setIsVisible(visibility && currentIndex === index)}>
                <Box key={index} w={'100%'} height={'96'} zIndex={99999}>
                    <ReactPlayer url={item.result_url} width={'100%'} height={'100%'} playing={isVisible} loop muted controls />
                </Box>
            </VisibilitySensor>
        );
    }

    if (item?.media_type === 'image') {

        const imgRef = useRef(null);

        useEffect(() => {

            const handleResize = () => {

                if (imgRef.current) {
                    setImgHeight(imgRef.current.clientHeight);
                }
            };

            if (imgRef.current) {
                setImgHeight(imgRef.current.clientHeight);
            }

            window.addEventListener('resize', handleResize);
        }, [imgRef.current]);

        const handleImageLoad = () => {

            if (imgRef.current) {
                setImgHeight(imgRef.current.clientHeight);
            }
        };

        return <img key={index} alt={''} ref={imgRef} src={item.result_url} width={'100%'} onLoad={handleImageLoad} loading='lazy' />;
    }

    return (
        <Box key={index} w={'100%'} height={96} zIndex={99999}>
            <Image
                w={'100%'}
                h={'96'}
                uri={'https://www.foot.com/wp-content/uploads/2017/06/placeholder-square.jpg'} // TODO: wtf is this?
            />
        </Box>
    );
};
